"use client";

import { config } from "@/config/env";
import type { LoginSuccessResponse } from "./types";

const EXCHANGE_URL = `${config.NEXT_PUBLIC_AI_DASHBOARD_API_URL}/auth/exchange`;

// Parent domain for the shared SSO cookies, derived per-request from the host so one
// build works on both l2tmedia.com and l2t.ai. Null = host-only (e.g. localhost).
function authCookieDomain(): string | null {
    if (typeof window === "undefined") return null;
    const host = window.location.hostname;
    if (host === "l2t.ai" || host.endsWith(".l2t.ai")) return ".l2t.ai";
    if (host === "l2tmedia.com" || host.endsWith(".l2tmedia.com")) return ".l2tmedia.com";
    return null;
}

function cookieAttributes(clear = false): string {
    const maxAge = clear ? "Max-Age=0" : "Max-Age=86400";
    const domain = authCookieDomain();
    if (!domain) {
        return `SameSite=Lax; ${maxAge}; Path=/`;
    }
    return `Domain=${domain}; Secure; SameSite=Lax; ${maxAge}; Path=/`;
}

export function getSsoCookies(): { mapToken: string; userGuid: string } | null {
    if (typeof document === "undefined") return null;
    const mapToken = document.cookie.match(/(?:^|;\s*)sso_map_token=([^;]+)/)?.[1];
    const userGuid = document.cookie.match(/(?:^|;\s*)sso_user_guid=([^;]+)/)?.[1];
    if (!mapToken || !userGuid) return null;
    return { mapToken: decodeURIComponent(mapToken), userGuid: decodeURIComponent(userGuid) };
}

export function setSsoCookies(mapToken: string, userGuid: string): void {
    document.cookie = `sso_map_token=${encodeURIComponent(mapToken)}; ${cookieAttributes()}`;
    document.cookie = `sso_user_guid=${encodeURIComponent(userGuid)}; ${cookieAttributes()}`;
}

export function clearSsoCookies(): void {
    document.cookie = `sso_map_token=; ${cookieAttributes(true)}`;
    document.cookie = `sso_user_guid=; ${cookieAttributes(true)}`;
}

export async function resumeFromCookies(): Promise<LoginSuccessResponse | null> {
    const cookies = getSsoCookies();
    if (!cookies) return null;
    try {
        const res = await fetch(EXCHANGE_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ target: "dashboard", ...cookies }),
        });
        if (!res.ok) {
            clearSsoCookies();
            return null;
        }
        const data: LoginSuccessResponse = await res.json();
        setSsoCookies(data.mapToken, data.authData.userId);
        return data;
    } catch {
        return null;
    }
}
